/**
 * Analytics.tsx — T318 / FR-007 + FR-016 + FR-021 wired surface.
 *
 * Replaces the prototype's CIRCA-series + pseudo-random sparkline reads
 * with reactive Convex subscriptions on the real analytics rollups
 * (analytics.getWatchTimeSummary, analytics.getTopChannelsByWatchTime,
 * analytics.getScheduleItemStatusCounts).
 *
 * The freshness banner binds the eleven-key catalogue (FR-007) — the
 * key for the analytics rollup is hourly-analytics-rollup, which is the
 * cron that populates sloDaily (the watch-time read source). A second
 * banner pins slo-daily-rollup for the daily-rollup-latency dimension
 * noted in freshness_facts §4.
 *
 * FR-016 dash glyph: the analytics surface has no public quality read
 * (the per-channel quality read lives at useChannelCard); whenever a
 * top-channel row returns no channelName the surface renders the
 * em-dash glyph for that field rather than a fabricated placeholder.
 *
 * FR-021 invariants (pinned by the observability contract tests):
 *
 *   - Imports useQuery from convex react — never reads from the
 *     prototype mock store.
 *   - No pseudo-random metrics anywhere. The prototype's sparkline
 *     composition has been removed in favour of the status-count
 *     buckets surfaced by the real schedule-item read.
 *   - No manual-refresh affordance: the page does not render a
 *     page-level refresh button. Data refreshes reactively when the
 *     underlying rollup updates.
 *
 * Honest-empty handling: a row with totalSegments at zero renders 0 h,
 * not a placeholder; a channel without a joinable channelName renders
 * the em-dash glyph rather than a null literal. A summary that is
 * still loading renders the Skeleton, never a fabricated number.
 */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  EmptyState,
  FreshnessBanner,
  PageHead,
  Panel,
  Pill,
  Skeleton,
  StatCard,
} from '../../components/primitives';

// ---------------------------------------------------------------------------
// Local types — mirror the Convex query return shapes so callers in this
// file stay strict-mode clean without leaning on the generated `api` types
// at the call site (which return `unknown` under the test mock).
// ---------------------------------------------------------------------------

interface WatchTimeSummary {
  watchTimeSeconds: number;
  totalSegments: number;
  rangeDays: number;
}

interface TopChannelEntry {
  channelId: string;
  channelName: string | null;
  totalSegments: number;
  watchTimeSeconds: number;
}

interface StatusCounts {
  pending: number;
  playing: number;
  live_active: number;
  completed: number;
  skipped: number;
  error: number;
}

const RANGE_DAYS = 30;

function formatHours(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0 h';
  const hours = Math.round(seconds / 3600);
  return `${hours.toLocaleString('es-MX')} h`;
}

export function Analytics() {
  const summary = useQuery(api.analytics.getWatchTimeSummary, {
    rangeDays: RANGE_DAYS,
  }) as WatchTimeSummary | undefined;

  const topChannels = useQuery(api.analytics.getTopChannelsByWatchTime, {
    topN: 5,
    rangeDays: RANGE_DAYS,
  }) as TopChannelEntry[] | undefined;

  const statusCounts = useQuery(api.analytics.getScheduleItemStatusCounts, {
    rangeDays: RANGE_DAYS,
  }) as StatusCounts | undefined;

  const loading =
    summary === undefined ||
    topChannels === undefined ||
    statusCounts === undefined;

  return (
    <div>
      <PageHead
        title="Analíticas"
        description="Tiempo de visualización por canal y rendimiento de la grilla."
      />
      <FreshnessBanner cronKey="hourly-analytics-rollup" />
      <FreshnessBanner cronKey="slo-daily-rollup" />

      {loading ? (
        <>
          <div className="circa-grid-4" style={{ marginBottom: 24 }}>
            <Skeleton h={92} />
            <Skeleton h={92} />
            <Skeleton h={92} />
            <Skeleton h={92} />
          </div>
          <Skeleton h={220} />
        </>
      ) : (
        <>
          <div className="circa-grid-4" style={{ marginBottom: 24 }}>
            <StatCard
              label="Tiempo de visualización"
              value={formatHours(summary.watchTimeSeconds)}
              hint={`últimos ${summary.rangeDays} días`}
              icon="clock"
            />
            <StatCard
              label="Bloques en grilla"
              value={statusCounts.completed.toLocaleString('es-MX')}
              hint="completados"
              icon="checkCircle"
              tone={statusCounts.completed > 0 ? 'good' : undefined}
            />
            <StatCard
              label="En curso"
              value={(
                statusCounts.playing + statusCounts.live_active
              ).toLocaleString('es-MX')}
              icon="radio"
              tone="info"
            />
            <StatCard
              label="Con falla"
              value={statusCounts.error.toLocaleString('es-MX')}
              icon="alertCircle"
              tone={statusCounts.error > 0 ? 'bad' : undefined}
            />
          </div>

          <Panel
            title="Canales principales"
            right={
              <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
                últimos {summary.rangeDays} días · top {topChannels.length}
              </span>
            }
            pad={0}
            style={{ marginBottom: 24 }}
          >
            {topChannels.length === 0 ? (
              <div style={{ padding: 24 }}>
                <EmptyState
                  icon="bar-chart"
                  title="Sin datos de visualización"
                  body="Aún no hay tiempo de visualización registrado en el periodo."
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {topChannels.map((row, i) => (
                  <div
                    key={row.channelId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '13px 20px',
                      borderBottom:
                        i < topChannels.length - 1
                          ? '1px solid var(--lightest-gray)'
                          : 'none',
                    }}
                  >
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 12,
                        color: 'var(--muted-slate)',
                        width: 16,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span style={{ flex: 1, fontSize: 14 }}>
                      {row.channelName ?? '—'}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>
                      {formatHours(row.watchTimeSeconds)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Estado de bloques en la grilla" pad={20}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <Pill tone="neutral">
                Pendientes: {statusCounts.pending.toLocaleString('es-MX')}
              </Pill>
              <Pill tone="live">
                Reproduciendo: {statusCounts.playing.toLocaleString('es-MX')}
              </Pill>
              <Pill tone="live">
                En vivo: {statusCounts.live_active.toLocaleString('es-MX')}
              </Pill>
              <Pill tone="good">
                Completados: {statusCounts.completed.toLocaleString('es-MX')}
              </Pill>
              <Pill tone="warn">
                Saltados: {statusCounts.skipped.toLocaleString('es-MX')}
              </Pill>
              <Pill tone="bad">
                Con falla: {statusCounts.error.toLocaleString('es-MX')}
              </Pill>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

export default Analytics;
